/**
 * Meta-Health Clinical Data-Point Schema
 * Based on JSON Schema Draft 2020-12
 */

export const dataPointSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'https://meta-health.co.za/schemas/data-point.schema.json',
  title: 'Meta-Health Clinical Data-Point',
  type: 'object',
  additionalProperties: false,

  // Required core attributes
  required: ['id', 'label', 'category', 'fieldType', 'isActive'],
  properties: {
    id: { type: 'string', pattern: '^[a-z0-9_]+$' },
    label: { type: 'string', minLength: 1 },
    category: { type: 'string', minLength: 1 },
    subcategory: { type: 'string' },
    fieldType: {
      type: 'string',
      enum: ['checkbox', 'single', 'multi', 'numeric', 'text', 'file', 'formula', 'panel', 'date', 'datetime', 'series']
    },
    isActive: { type: 'boolean', default: true },

    // Presentation helpers
    placeholder: { type: 'string' },
    prefix: { type: 'string', default: '' },
    suffix: { type: 'string', default: '' },
    ui: {
      type: 'object',
      additionalProperties: false,
      properties: {
        span: { type: 'integer', minimum: 1, maximum: 12, default: 12 },
        variant: { type: 'string' },
        display: { type: 'string' } // chip | slider | search-select
      }
    },

    // Input type configuration for template builder
    inputConfig: {
      type: 'object',
      additionalProperties: false,
      properties: {
        inputType: {
          type: 'string',
          enum: ['dropdown', 'radio', 'checkbox', 'text', 'numeric'],
          default: 'dropdown'
        },
        multiSelect: { type: 'boolean', default: false },
        allowOther: { type: 'boolean', default: false },
        required: { type: 'boolean', default: false },
        defaultValue: { type: ['string', 'array', 'number', 'boolean', 'null'] },
        placeholder: { type: 'string' },
        helpText: { type: 'string' }
      }
    },

    // Value, options, validation
    default: { type: ['string', 'number', 'boolean', 'null'] },
    options: {
      type: 'array',
      items: { type: 'string' },
      minItems: 1,
      uniqueItems: true
    },
    units: { type: ['string', 'null'] },

    validation: {
      type: 'object',
      additionalProperties: false,
      properties: {
        min: { type: ['number', 'null'] },
        max: { type: ['number', 'null'] },
        pattern: { type: ['string', 'null'] },
        decimals: { type: ['integer', 'null'], minimum: 0 }
      }
    },

    referenceRanges: {
      type: 'array',
      items: {
        type: 'object',
        required: ['label'],
        additionalProperties: false,
        properties: {
          label: { type: 'string' }, // Low | Normal | High
          min: { type: ['number', 'null'] },
          max: { type: ['number', 'null'] },
          sex: { enum: ['M', 'F', null] },
          ageMin: { type: ['number', 'null'] },
          ageMax: { type: ['number', 'null'] }
        }
      }
    },

    // Advanced / composite items
    nestedSubFields: { type: 'boolean', default: false },
    delimiter: { type: 'string', default: '/' },
    subFields: {
      type: 'array',
      items: { $ref: '#' } // recursive definition
    },
    formula: { type: 'string' },

    fileSpec: {
      type: 'object',
      additionalProperties: false,
      properties: {
        maxSizeMB: { type: 'number', minimum: 0 },
        allowed: {
          type: 'array',
          items: { type: 'string' },
          uniqueItems: true
        }
      }
    },

    // Mapping & audit
    code: { type: 'string' }, // SNOMED / LOINC / CPT / ICD
    labCode: { type: ['string', 'null'] },
    searchSource: { type: 'string' }, // e.g. "icd10", "rxnorm"

    createdAt: { type: 'string', format: 'date-time' },
    updatedAt: { type: 'string', format: 'date-time' }
  }
}

export default dataPointSchema
